/**
 * The registry of guided creation flows.
 *
 * Adding a flow is: write the definition, add it here. The tour picks up the
 * button automatically because the engine asks this module — by tour-step id —
 * rather than carrying a list of its own.
 */

import { AUTOMATION_FLOW_TASK } from './automationFlow';
import { CREATE_FORM_TASK } from './createForm';
import { NEW_CAMPAIGNS_MODULE_TASK } from './newCampaignsModule';
import { SOCIAL_MEDIA_POST_TASK } from './socialMediaPost';
import { validateTaskFlows, type TaskFlow } from './types';

/**
 * Order is authoring order only — each flow is reached from its own tour stop, so
 * nothing here decides when one runs. Kept in the order the sidebar shows them so
 * the list reads the way the product does.
 */
export const TASKS: TaskFlow[] = [
  // 🔴 `MESSAGE_TEMPLATE_TASK` and `WHATSAPP_CAMPAIGN_TASK` ARE DELIBERATELY NOT
  // REGISTERED. They hang off `campaigns--message-templates` and
  // `campaigns--whatsapp-campaigns`, two tour stops that no longer exist now that
  // the `marketing` sidebar group is hidden — `getTaskForStep` would never match
  // either, so registering them adds two flows nothing can reach.
  //
  // The files stay (`messageTemplate.ts`, `whatsappCampaign.ts`) because they are
  // written against the CURRENT Campaigns and Templates pages, which still ship and
  // still work on a typed URL. Re-import both here the day those sidebar rows and
  // their tour stops come back — do not rewrite them to serve the redesign, which
  // is what the flow below is for: different components, different markup, and one
  // flow trying to serve both would break whichever page it was not written against.
  NEW_CAMPAIGNS_MODULE_TASK,
  SOCIAL_MEDIA_POST_TASK,
  AUTOMATION_FLOW_TASK,
  CREATE_FORM_TASK,
];

/** The flow offered on a tour step, or null when that step has none. */
export function getTaskForStep(stepId: string | null | undefined): TaskFlow | null {
  if (!stepId) return null;
  return TASKS.find((task) => task.stepId === stepId) ?? null;
}

export function getTask(taskId: string): TaskFlow | null {
  return TASKS.find((task) => task.id === taskId) ?? null;
}

/** Authoring mistakes. Called from the provider in development only. */
export function validateTasks(): string[] {
  return validateTaskFlows(TASKS);
}

// 🔴 `MESSAGE_TEMPLATE_TASK` and `WHATSAPP_CAMPAIGN_TASK` ARE NOT RE-EXPORTED —
// their imports were removed above, and naming them here compiled to
// "Cannot find name", which fails the whole app's typecheck rather than just this
// module. Re-add both to the import list and to this block together, or to neither.
export {
  AUTOMATION_FLOW_TASK,
  CREATE_FORM_TASK,
  NEW_CAMPAIGNS_MODULE_TASK,
  SOCIAL_MEDIA_POST_TASK,
};
export type { TaskFlow, TaskStep, CalloutSide } from './types';
